window.theme = window.theme || {};

theme.events = {
  on(eventName, callback) {
    document.addEventListener(eventName, callback);
  },

  off(eventName, callback) {
    document.removeEventListener(eventName, callback);
  },

  emit(eventName, detail = {}) {
    document.dispatchEvent(new CustomEvent(eventName, { detail }));
  }
};

theme.utils = {
  trapFocus(container) {
    if (!container) return () => {};

    const focusable = container.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    const handleKeydown = (event) => {
      if (event.key !== 'Tab' || !first || !last) return;

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    container.addEventListener('keydown', handleKeydown);
    first?.focus();

    return () => container.removeEventListener('keydown', handleKeydown);
  },

  lockScroll() {
    document.documentElement.classList.add('is-scroll-locked');
    document.body.classList.add('is-scroll-locked');
  },

  unlockScroll() {
    document.documentElement.classList.remove('is-scroll-locked');
    document.body.classList.remove('is-scroll-locked');
  }
};

theme.scroll = {
  init() {
    const update = () => {
      const scrollY = window.scrollY || window.pageYOffset;
      document.documentElement.classList.toggle('is-scrolled', scrollY > 16);
      document.documentElement.style.setProperty('--scroll-y', String(scrollY));
      theme.events.emit('theme:scroll', { scrollY });
    };

    update();
    let ticking = false;
    window.addEventListener('scroll', () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(() => {
        update();
        ticking = false;
      });
    }, { passive: true });
  }
};

theme.animations = {
  init() {
    this.prepareTextReveal();
    this.prepareStaggers();

    const revealItems = document.querySelectorAll('[data-animate], [data-text-reveal], [data-stagger]');
    if (!revealItems.length) return;

    if (!('IntersectionObserver' in window)) {
      revealItems.forEach((item) => item.classList.add('is-visible'));
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -12% 0px', threshold: 0.12 }
    );

    revealItems.forEach((item) => observer.observe(item));
  },

  prepareTextReveal() {
    const candidates = document.querySelectorAll(
      '.hero-section__heading, .type-title, .brand-story__heading, .editorial-page__intro h1, .contact-editorial__intro h1, .faq-editorial__intro h1, .main-product__title, .home-campaign-banner__content h2, .editorial-story-strip__content h2, .editorial-tile__content h3, .como-homepage__heading, .como-homepage__story-copy h2'
    );

    candidates.forEach((element) => {
      if (element.dataset.textRevealPrepared === 'true') return;
      if (element.children.length > 0) return;

      const text = element.textContent.trim();
      if (!text) return;

      element.dataset.textReveal = '';
      element.dataset.textRevealPrepared = 'true';
      element.textContent = '';

      text.split(/\s+/).forEach((word, index, words) => {
        const span = document.createElement('span');
        span.className = 'text-reveal__word';
        span.style.setProperty('--word-index', index);
        span.textContent = word;
        element.appendChild(span);

        if (index < words.length - 1) {
          element.appendChild(document.createTextNode(' '));
        }
      });
    });
  },

  prepareStaggers() {
    const containers = document.querySelectorAll(
      '.main-collection__grid, .product-grid-section__grid, .editorial-featured-products__grid, .featured-products__scroller, .editorial-story-strip, .editorial-collection-grid__items, .faq-editorial__items, .site-footer__links'
      + ', .como-homepage__grid, .como-homepage__categories, .como-homepage__ugc-grid'
    );

    containers.forEach((container) => {
      if (container.dataset.staggerPrepared === 'true') return;

      container.dataset.stagger = '';
      container.dataset.staggerPrepared = 'true';

      Array.from(container.children).forEach((child, index) => {
        child.style.setProperty('--stagger-index', index);
      });
    });
  }
};

theme.pageTransitions = {
  init() {
    document.body.classList.add('is-loading');
    window.requestAnimationFrame(() => {
      document.body.classList.remove('is-loading');
    });

    document.addEventListener('click', (event) => {
      const link = event.target.closest('a[href]');
      if (!link || event.defaultPrevented) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      if (link.target && link.target !== '_self') return;
      if (link.hasAttribute('download')) return;

      const url = new URL(link.href, window.location.href);
      if (url.origin !== window.location.origin) return;
      if (url.pathname === window.location.pathname && url.search === window.location.search && url.hash) return;

      event.preventDefault();
      document.body.classList.add('is-leaving');

      window.setTimeout(() => {
        window.location.href = url.href;
      }, 180);
    });

    window.addEventListener('pageshow', () => {
      document.body.classList.remove('is-leaving', 'is-loading');
    });
  }
};

theme.menu = {
  activeCleanup: null,

  init() {
    document.querySelectorAll('.theme-menu-drawer__nav').forEach((nav) => {
      Array.from(nav.children).forEach((link, index) => {
        link.style.setProperty('--drawer-index', index);
      });
    });

    document.querySelectorAll('[data-menu-toggle]').forEach((toggle) => {
      const targetSelector = toggle.getAttribute('data-menu-toggle');
      const target = targetSelector ? document.querySelector(targetSelector) : document.querySelector('[data-menu-drawer]');
      if (!target) return;

      toggle.addEventListener('click', () => {
        const isOpen = target.getAttribute('aria-hidden') === 'false';
        isOpen ? this.close(target, toggle) : this.open(target, toggle);
      });
    });

    document.querySelectorAll('[data-menu-close]').forEach((closeButton) => {
      closeButton.addEventListener('click', () => {
        const drawer = closeButton.closest('[data-menu-drawer]');
        this.close(drawer);
      });
    });
  },

  open(drawer, toggle) {
    if (!drawer) return;
    drawer.setAttribute('aria-hidden', 'false');
    drawer.classList.add('is-open');
    toggle?.setAttribute('aria-expanded', 'true');
    document.querySelector('[data-theme-header]')?.classList.add('is-drawer-open');
    theme.utils.lockScroll();
    this.activeCleanup = theme.utils.trapFocus(drawer);
    theme.events.emit('theme:menu:open', { drawer });
  },

  close(drawer, toggle) {
    if (!drawer) return;
    drawer.setAttribute('aria-hidden', 'true');
    drawer.classList.remove('is-open');
    const controls = drawer.id ? document.querySelectorAll(`[data-menu-toggle="#${drawer.id}"]`) : [];
    controls.forEach((control) => control.setAttribute('aria-expanded', 'false'));
    toggle?.setAttribute('aria-expanded', 'false');
    document.querySelector('[data-theme-header]')?.classList.remove('is-drawer-open');
    theme.utils.unlockScroll();
    this.activeCleanup?.();
    this.activeCleanup = null;
    theme.events.emit('theme:menu:close', { drawer });
  }
};

theme.cartDrawer = {
  drawer: null,
  cleanupFocus: null,
  moneyFormat: '${{amount}}',
  catalogPromise: null,
  productCache: new Map(),
  lastCart: null,
  couponCleanup: null,
  checkoutCouponCode: '',

  init() {
    this.drawer = document.querySelector('[data-cart-drawer]');
    if (!this.drawer) return;

    document.querySelectorAll('[data-cart-open]').forEach((button) => {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        this.open();
      });
    });

    document.querySelectorAll('[data-cart-close]').forEach((button) => {
      button.addEventListener('click', () => this.close());
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        this.closeCouponModal();
        this.closeCouponConfirm();
        this.close();
      }
    });

    document.addEventListener('click', (event) => {
      if (event.target.closest('[data-cart-summary-open]')) {
        event.preventDefault();
        theme.priceBreakdown?.open?.();
        return;
      }

      if (event.target.closest('[data-cart-coupons-open]')) {
        event.preventDefault();
        this.openCouponModal();
        return;
      }

      const couponButton = event.target.closest('[data-cart-coupon-apply]');
      if (couponButton) {
        event.preventDefault();
        this.applyCoupon(couponButton.getAttribute('data-cart-coupon-apply'));
        return;
      }

      if (event.target.closest('[data-cart-coupon-submit]')) {
        event.preventDefault();
        this.applyCoupon(document.querySelector('[data-cart-coupon-input]')?.value || '');
        return;
      }

      if (event.target.closest('[data-cart-coupon-close]')) {
        event.preventDefault();
        this.closeCouponModal();
        return;
      }

      if (event.target.closest('[data-cart-coupon-confirm-close]')) {
        event.preventDefault();
        this.closeCouponConfirm();
        return;
      }

      const copyButton = event.target.closest('[data-copy-code]');
      if (copyButton) {
        event.preventDefault();
        this.copyCouponCode(copyButton);
      }
    });

    this.drawer.addEventListener('click', (event) => {
      const summaryToggle = event.target.closest('[data-cod-cart-summary-toggle]');
      if (summaryToggle) {
        event.preventDefault();
        this.toggleInlineSummary(summaryToggle);
        return;
      }

      const recommendationButton = event.target.closest('[data-cart-rec-add]');
      if (recommendationButton) {
        event.preventDefault();
        this.addRecommendation(recommendationButton);
        return;
      }

      const item = event.target.closest('[data-cart-item]');
      if (!item) return;

      // A drawer row is a GROUP of server lines (BUY 2 GET 1 FREE splits one variant
      // across several, and the split moves). Edits go through setGroupQuantity so the
      // row's true quantity is what changes -- never a single split line.
      if (event.target.matches('[data-cart-increase]')) {
        this.setGroupQuantity(item, Number(item.dataset.quantity) + 1);
      }

      if (event.target.matches('[data-cart-decrease]')) {
        this.setGroupQuantity(item, Math.max(0, Number(item.dataset.quantity) - 1));
      }

      if (event.target.matches('[data-cart-remove]')) {
        this.setGroupQuantity(item, 0);
      }
    });

    this.drawer.addEventListener('change', (event) => {
      const input = event.target.closest('[data-cart-item-quantity]');
      if (!input) return;
      const item = input.closest('[data-cart-item]');
      if (!item) return;
      const min = Number(input.min || 1);
      const max = Number(input.max || 10);
      const quantity = Math.max(min, Math.min(max, Number(input.value || 1)));
      this.setGroupQuantity(item, quantity);
    });

    document.addEventListener('submit', (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement) || form.action.indexOf('/cart/add') === -1) return;
      if (!form.matches('[data-ajax-cart-form], .tf-card__form, .blacklist-product-v1__form')) return;

      event.preventDefault();
      this.addFromForm(form);
    });
  },

  async open() {
    if (!this.drawer) return;
    this.drawer.setAttribute('aria-hidden', 'false');
    this.drawer.classList.add('is-open');
    theme.utils.lockScroll();
    this.cleanupFocus = theme.utils.trapFocus(this.drawer);
    await this.refresh();
    theme.events.emit('theme:cart:open', { drawer: this.drawer });
  },

  close() {
    if (!this.drawer) return;
    this.drawer.setAttribute('aria-hidden', 'true');
    this.drawer.classList.remove('is-open');
    theme.utils.unlockScroll();
    this.cleanupFocus?.();
    this.cleanupFocus = null;
    theme.events.emit('theme:cart:close', { drawer: this.drawer });
  },

  async addFromForm(form) {
    const submit = form.querySelector('[type="submit"]');
    submit?.setAttribute('disabled', 'disabled');

    try {
      await this.fetchJson('/cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(form)
      });
      await this.open();
    } catch (error) {
      console.warn('Cart add failed', error);
    } finally {
      submit?.removeAttribute('disabled');
    }
  },

  async changeItem(key, quantity) {
    try {
      await this.fetchJson('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({ id: key, quantity })
      });
      await this.refresh();
    } catch (error) {
      console.warn('Cart change failed', error);
    }
  },

  // Set a drawer row's TOTAL quantity. Collapsing the group's other keys to 0 and
  // putting the whole quantity on the first key lets Shopify re-split the BUY 2 GET 1
  // FREE however it likes without us losing units. Addressing by KEY (never index)
  // survives the split moving between renders.
  async setGroupQuantity(row, quantity) {
    const keys = String((row && (row.dataset.keys || row.dataset.key)) || '').split(',').filter(Boolean);
    if (!keys.length) return;
    try {
      const updates = {};
      keys.forEach((key, i) => { updates[key] = i === 0 ? quantity : 0; });
      await this.fetchJson('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ updates })
      });
      await this.refresh();
    } catch (error) {
      console.warn('Cart change failed', error);
    }
  },

  async refresh() {
    const cart = await this.fetchCart();
    this.render(cart);
    theme.events.emit('theme:cart:updated', { cart });
  },

  async fetchCart() {
    const response = await fetch(`/cart.js?ts=${Date.now()}`, {
      headers: { Accept: 'application/json' },
      cache: 'no-store'
    });
    if (!response.ok) throw new Error(`Cart fetch failed: ${response.status}`);
    return response.json();
  },

  async fetchJson(url, options = {}) {
    const response = await fetch(url, options);
    if (!response.ok) throw new Error(`Request failed: ${response.status}`);
    return response.json();
  },

	render(cart) {
	  this.lastCart = cart;
    const itemsContainer = this.drawer.querySelector('[data-cart-items]');
    const footer = this.drawer.querySelector('[data-cart-footer]');
    const subtotal = this.drawer.querySelector('[data-cart-subtotal]');
    const template = document.querySelector('[data-cart-item-template]');

    itemsContainer.innerHTML = '';

	  if (!cart.items.length) {
	    itemsContainer.innerHTML = '<p class="cart-drawer__empty">Your cart is empty.</p>';
	    footer.hidden = true;
	    this.renderRecommendations(cart);
	    this.updateCartCount(cart.item_count);
	    const drawerCount = this.drawer.querySelector('[data-cart-drawer-count]');
	    if (drawerCount) drawerCount.textContent = '(0)';
	    this.closeCouponOverlays({ skipReload: true });
	    this.updateCouponState(cart);
	    return;
	  }

    // BUY 2 GET 1 FREE splits ONE variant across several server lines and the split
    // MOVES (measured: qty 3 -> 2 lines, qty 4 -> 3 lines, qty 6 -> 2 lines, and in a
    // mixed cart the free line comes first). groupsOf folds them back into one row per
    // variant carrying its true quantity plus the KEYS needed to update it. The fallback
    // only runs if snippets/bogo-offer.liquid is ever removed.
    const groups = window.BogoOffer
      ? window.BogoOffer.groupsOf(cart)
      : cart.items.map((it) => ({
          first: it, qty: it.quantity, orig: it.original_line_price,
          final: it.final_line_price, freeUnits: it.final_line_price === 0 ? it.quantity : 0,
          keys: [it.key]
        }));

    groups.forEach((group, index) => {
      const lineItem = group.first;
      const node = template.content.firstElementChild.cloneNode(true);
      node.dataset.key = group.keys[0];
      node.dataset.keys = group.keys.join(',');
      node.dataset.quantity = group.qty;
      node.style.setProperty('--be-motion-index', index);

      const image = node.querySelector('[data-cart-item-image]');
      const imageLink = node.querySelector('[data-cart-item-url]');
      imageLink.href = lineItem.url;
      if (lineItem.image) {
        image.src = `${lineItem.image}&width=180`;
        image.alt = lineItem.product_title;
      } else {
        image.remove();
      }

      node.querySelector('[data-cart-item-title]').textContent = lineItem.product_title;
      node.querySelector('[data-cart-item-title]').href = lineItem.url;
      node.querySelector('[data-cart-item-variant]').textContent = lineItem.variant_title || '';
      const quantity = node.querySelector('[data-cart-item-quantity]');
      if (quantity) {
        if ('value' in quantity) quantity.value = group.qty;
        quantity.textContent = group.qty;
      }
      // original_price, not final_price: the group's first server line can be the FREE
      // one, and a unit price of INR 0 beside a paying quantity reads as a pricing bug.
      const unit = node.querySelector('[data-cart-item-unit]');
      if (unit) unit.innerHTML = this.formatMoneyHtml(lineItem.original_price);

      const priceEl = node.querySelector('[data-cart-item-price]');
      if (group.orig > group.final && group.final === 0) {
        priceEl.innerHTML = `<span class="bo-was">${this.formatMoneyHtml(group.orig)}</span><br><span class="bo-freepill">FREE</span>`;
      } else if (group.orig > group.final) {
        priceEl.innerHTML = `<span class="bo-was">${this.formatMoneyHtml(group.orig)}</span><br><span>${this.formatMoneyHtml(group.final)}</span>`;
      } else {
        priceEl.innerHTML = this.formatMoneyHtml(group.final);
      }

      // BIRTHDAY SALE tag (snippets/bogo-offer.liquid). Pass the group's FREE server line
      // so isFreeLine() gates on a non-zero allocation -- the paying line also carries an
      // allocation, at amount 0. Optional by design: if the snippet is ever removed the
      // drawer just renders without the tag.
      const freeLine = window.BogoOffer
        ? (cart.items || []).find((it) => group.keys.indexOf(it.key) > -1 && window.BogoOffer.isFreeLine(it))
        : null;
      if (window.BogoOffer) window.BogoOffer.decorate(node, freeLine || lineItem, cart, '[data-bo-slot]');

      itemsContainer.appendChild(node);
    });

    subtotal.innerHTML = this.formatMoneyHtml(cart.total_price);
    // BIRTHDAY SALE progress nudge, placed just above the order-summary toggle.
    if (window.BogoOffer) window.BogoOffer.renderSummary(footer, cart, '.cart-drawer__summary-toggle');
    this.updateInlineSummary(cart);
    const payTotal = this.drawer.querySelector('[data-cart-pay-total]');
    if (payTotal) payTotal.innerHTML = this.formatMoneyHtml(cart.total_price);
    const drawerCount = this.drawer.querySelector('[data-cart-drawer-count]');
    if (drawerCount) drawerCount.textContent = `(${cart.item_count})`;
    footer.hidden = false;
    this.renderRecommendations(cart);
    this.updateCartCount(cart.item_count);
    this.updateCouponState(cart);
  },

  productHandleFromItem(lineItem) {
    if (lineItem.handle) return lineItem.handle;
    const match = String(lineItem.url || '').match(/\/products\/([^/?#]+)/);
    return match ? decodeURIComponent(match[1]) : '';
  },

  async loadCatalog() {
    if (!this.drawer) return { products: [] };
    if (!this.catalogPromise) {
      const url = this.drawer.getAttribute('data-catalog-index');
      this.catalogPromise = fetch(url, { credentials: 'same-origin' })
        .then((response) => response.ok ? response.json() : Promise.reject(response.status))
        .catch(() => ({ products: [] }));
    }
    return this.catalogPromise;
  },

  async fetchProductByHandle(handle) {
    const key = String(handle || '').trim();
    if (!key) return null;
    if (!this.productCache.has(key)) {
      this.productCache.set(key, this.fetchJson(`/products/${encodeURIComponent(key)}.js`, {
        headers: { Accept: 'application/json' }
      }).catch(() => null));
    }
    return this.productCache.get(key);
  },

  async fetchShopifyRecommendations(productId) {
    if (!productId) return [];
    try {
      const data = await this.fetchJson(`/recommendations/products.json?product_id=${encodeURIComponent(productId)}&limit=8`, {
        headers: { Accept: 'application/json' }
      });
      return Array.isArray(data.products) ? data.products : [];
    } catch (error) {
      return [];
    }
  },

  productUrl(handle) {
    return `/products/${encodeURIComponent(handle)}`;
  },

  imageWithWidth(src, width = 900) {
    const value = String(src || '');
    if (!value) return '';
    if (!/cdn\.shopify\.com/.test(value)) return value;
    return value.includes('?') ? `${value}&width=${width}` : `${value}?width=${width}`;
  },

  normalizeProduct(raw) {
    if (!raw) return null;
    const handle = raw.handle || raw.h || '';
    if (!handle) return null;
    const variants = Array.isArray(raw.variants) ? raw.variants : [];
    const selected = variants.find((variant) => variant.available) || variants[0] || null;
    const rawImages = Array.isArray(raw.images) ? raw.images : [];
    const images = rawImages
      .map((image) => {
        if (typeof image === 'string') return image;
        return image?.src || image?.url || '';
      })
      .filter(Boolean);
    const featured = raw.featured_image?.src || raw.featured_image || raw.img || images[0] || '';
    if (!images.length && featured) images.push(featured);
    return {
      id: raw.id || raw.product_id || '',
      handle,
      title: raw.title || raw.t || '',
      type: raw.type || raw.ty || 'Product',
      price: Number(selected?.price ?? raw.price ?? 0),
      available: raw.available !== false && (variants.length ? variants.some((variant) => variant.available) : true),
      images,
      featured,
      variants,
      selected
    };
  },

  variantLabel(variant) {
    return String(variant?.option1 || variant?.title || 'Variant').replace(/^Default Title$/i, 'Add');
  },

  variantButtonsHtml(product) {
    if (!product.available || !product.variants.length) return '';
    const hasOnlyDefault = product.variants.length <= 1 && /^default title$/i.test(String(product.variants[0]?.title || ''));
    if (hasOnlyDefault) {
      const variant = product.selected || product.variants[0];
      if (!variant?.id) return '';
      return `
        <button
          type="button"
          class="CODProductCardVariant"
          data-card-variant-add="${this.escapeHtml(variant.id)}"
          data-card-variant-label="Add"
          aria-label="Add ${this.escapeHtml(product.title)} to cart"
        >Add</button>
      `;
    }

    const seen = new Set();
    return product.variants.map((variant) => {
      const label = this.variantLabel(variant);
      if (seen.has(label)) return '';
      seen.add(label);
      const preferred = product.variants.find((item) => this.variantLabel(item) === label && item.available) || variant;
      const disabled = preferred.available ? '' : ' disabled aria-disabled="true"';
      const aria = preferred.available
        ? `Add ${product.title} in ${label} to cart`
        : `${label} unavailable`;
      return `
        <button
          type="button"
          class="CODProductCardVariant${preferred.available ? '' : ' is-disabled'}"
          data-card-variant-add="${this.escapeHtml(preferred.id)}"
          data-card-variant-label="${this.escapeHtml(label)}"
          aria-label="${this.escapeHtml(aria)}"${disabled}
        >${this.escapeHtml(label)}</button>
      `;
    }).join('');
  },

  productCardHtml(rawProduct, index = 0) {
    const product = this.normalizeProduct(rawProduct);
    if (!product) return '';
    const url = this.productUrl(product.handle);
    const images = product.images.slice(0, 10);
    const imageSlides = images.length
      ? images.map((src) => `
          <span class="rv-cardcar__slide">
            <img
              class="CODProductCardImage myugen-collection-card__image"
              src="${this.escapeHtml(this.imageWithWidth(src, 900))}"
              alt="${this.escapeHtml(product.title)}"
              width="760"
              height="1140"
              loading="lazy"
              decoding="async"
            >
          </span>
        `).join('')
      : '<span class="rv-cardcar__slide"><span class="CODProductCardImage myugen-collection-card__image placeholder-svg"></span></span>';
    const nav = images.length > 1 ? `
      <button type="button" class="CODProductCardNav rv-cardcar__nav rv-cardcar__nav--prev" data-rv-prev aria-label="Previous image">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" focusable="false" aria-hidden="true"><path d="M15 6l-6 6 6 6" stroke-linecap="round" stroke-linejoin="round"></path></svg>
      </button>
      <button type="button" class="CODProductCardNav rv-cardcar__nav rv-cardcar__nav--next" data-rv-next aria-label="Next image">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" focusable="false" aria-hidden="true"><path d="M9 6l6 6-6 6" stroke-linecap="round" stroke-linejoin="round"></path></svg>
      </button>
      <span class="rv-cardcar__bar" data-rv-bar aria-hidden="true"><span class="rv-cardcar__bar-fill" data-rv-barfill></span></span>
    ` : '';
    const wishlistImage = this.imageWithWidth(product.featured || images[0], 800);
    return `
      <article
        class="CODProductCard myugen-collection-card cart-drawer__recommendation-card"
        data-component="CODProductCard"
        data-component-tier="tissue"
        data-product-handle="${this.escapeHtml(product.handle)}"
        style="--tf-card-index: ${Number(index) || 0};"
      >
        <div
          class="CODProductCardMedia myugen-collection-card__media rv-cardcar"
          data-component="CODProductCardMedia"
          data-rv-cardcar
          data-count="${images.length}"
        >
          <a class="CODProductCardMediaLink myugen-collection-card__media-link" href="${this.escapeHtml(url)}" aria-label="${this.escapeHtml(product.title)}">
            <span class="rv-cardcar__track" data-rv-track>${imageSlides}</span>
            <span class="u-visually-hidden">View ${this.escapeHtml(product.title)}</span>
          </a>
          ${nav}
          ${product.available ? '' : '<span class="CODProductBadge myugen-collection-card__badge">Sold out</span>'}
          <button
            class="CODWishlistButton myugen-collection-card__wishlist"
            type="button"
            data-wishlist-toggle
            data-product-id="${this.escapeHtml(product.id)}"
            data-product-handle="${this.escapeHtml(product.handle)}"
            data-product-title="${this.escapeHtml(product.title)}"
            data-product-url="${this.escapeHtml(url)}"
            data-product-price="${this.escapeHtml(product.price)}"
            data-product-image="${this.escapeHtml(wishlistImage)}"
            aria-label="Add ${this.escapeHtml(product.title)} to wishlist"
            aria-pressed="false"
          >
            <svg viewBox="0 0 24 24" focusable="false" aria-hidden="true"><path d="M12 21s-7.5-4.6-10-9.3C.5 8.5 2 5 5.2 5 7 5 8.4 6 12 9c3.6-3 5-4 6.8-4C22 5 23.5 8.5 22 11.7 19.5 16.4 12 21 12 21z"></path></svg>
          </button>
          <button
            class="CODQuickViewButton myugen-collection-card__quicklook"
            type="button"
            data-quick-view-handle="${this.escapeHtml(product.handle)}"
            aria-label="Quick add ${this.escapeHtml(product.title)}"
          >
            <svg viewBox="0 0 24 24" focusable="false" aria-hidden="true"><path d="M12 5v14M5 12h14"></path></svg>
          </button>
          <div class="CODProductCardVariants myugen-collection-card__variants" aria-label="Quick add ${this.escapeHtml(product.title)}">
            ${this.variantButtonsHtml(product)}
          </div>
        </div>
        <a class="CODProductCardBody myugen-collection-card__info" href="${this.escapeHtml(url)}" aria-label="${this.escapeHtml(product.title)}">
          <h2 class="CODProductCardTitle myugen-collection-card__title">${this.escapeHtml(product.title)}</h2>
          <span class="CODProductCardPrice myugen-collection-card__price">${this.formatMoneyHtml(product.price)}</span>
        </a>
      </article>
    `;
  },

  async renderRecommendations(cart) {
    const section = this.drawer?.querySelector('[data-cart-recommendations]');
    const grid = this.drawer?.querySelector('[data-cart-recommendation-grid]');
    if (!section || !grid) return;

    grid.innerHTML = '';

    if (!cart.items.length) {
      section.hidden = true;
      return;
    }

    const cartHandles = new Set(cart.items.map((item) => this.productHandleFromItem(item)).filter(Boolean));
    const shownHandles = new Set();
    const recommendationLists = await Promise.all(
      cart.items.slice(0, 4).map((item) => this.fetchShopifyRecommendations(item.product_id))
    );
    const picked = [];

    for (let rank = 0; rank < 8 && picked.length < 4; rank += 1) {
      for (const list of recommendationLists) {
        const product = list[rank];
        const handle = product?.handle;
        if (!handle || cartHandles.has(handle) || shownHandles.has(handle)) continue;
        picked.push(product);
        shownHandles.add(handle);
        if (picked.length >= 4) break;
      }
    }

    const data = await this.loadCatalog();
    const products = Array.isArray(data.products) ? data.products : [];
    const fallbackHandles = products
      .filter((product) => product && product.h && !cartHandles.has(product.h) && !shownHandles.has(product.h))
      .sort((a, b) => Number(b.p || 0) - Number(a.p || 0))
      .map((product) => product.h);

    for (const handle of fallbackHandles) {
      if (picked.length >= 4) break;
      const product = await this.fetchProductByHandle(handle);
      if (!product?.handle || cartHandles.has(product.handle) || shownHandles.has(product.handle)) continue;
      picked.push(product);
      shownHandles.add(product.handle);
    }

    const hydrated = await Promise.all(picked.slice(0, 4).map(async (product) => {
      if (product?.variants && product?.images) return product;
      return this.fetchProductByHandle(product.handle);
    }));
    const cards = hydrated
      .filter(Boolean)
      .map((product, index) => this.productCardHtml(product, index))
      .join('');

    grid.innerHTML = cards;
    section.hidden = cards.trim().length === 0;
    theme.cardCarousel?.scan?.(grid);
    theme.wishlist?.syncButtons?.();
  },

  async addRecommendation(button) {
    const handle = button.dataset.productHandle;
    if (!handle) return;
    button.disabled = true;
    try {
      await theme.quickView?.open?.(handle);
    } catch (error) {
      console.warn('Recommendation quick view failed', error);
    } finally {
      button.disabled = false;
    }
  },

  escapeHtml(value) {
    return String(value || '').replace(/[&<>"']/g, (char) => ({
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#39;'
    }[char]));
  },

  discountCodes(cart = this.lastCart) {
    const codes = [];
    (cart?.cart_level_discount_applications || []).forEach((discount) => codes.push(discount.title || discount.code));
    (cart?.discount_applications || []).forEach((discount) => codes.push(discount.title || discount.code));
    (cart?.items || []).forEach((item) => {
      (item.discounts || []).forEach((discount) => codes.push(discount.title || discount.code));
      (item.line_level_discount_allocations || []).forEach((allocation) => {
        const discount = allocation.discount_application || allocation.discountApplication || {};
        codes.push(discount.title || discount.code);
      });
    });
    return new Set(codes.map((code) => String(code || '').trim().toUpperCase()).filter(Boolean));
  },

	  updateCouponState(cart = this.lastCart) {
	    const applied = this.discountCodes(cart);
	    const hasItems = Number(cart?.item_count || 0) > 0 || Boolean(cart?.items?.length);
	    document.querySelectorAll('[data-cart-coupon-apply]').forEach((button) => {
	      const code = String(button.getAttribute('data-cart-coupon-apply') || '').trim().toUpperCase();
	      const active = hasItems && (applied.has(code) || button.classList.contains('is-applied'));
	      button.classList.toggle('is-applied', active);
	      button.textContent = active ? 'Applied' : 'Apply';
	      button.disabled = active;
	    });
	  },

	  closeCouponOverlays(options = {}) {
	    if (options.skipReload) {
	      delete document.body.dataset.cartReloadAfterCoupon;
	    }
	    this.closeCouponModal();
	    this.closeCouponConfirm();
	  },

  markCheckoutCoupon(code) {
    const normalized = String(code || this.checkoutCouponCode).trim().toUpperCase();
    document.querySelectorAll('input[name="discount"]').forEach((input) => {
      input.value = normalized;
    });
    document.querySelectorAll('[data-cart-coupon-apply]').forEach((button) => {
      const buttonCode = String(button.getAttribute('data-cart-coupon-apply') || '').trim().toUpperCase();
      if (buttonCode === normalized) {
        button.classList.add('is-applied');
        button.textContent = 'Applied';
        button.disabled = true;
      }
    });
  },

  toggleInlineSummary(button) {
    const body = this.drawer?.querySelector('[data-cod-cart-summary-body]');
    if (!body) return;
    const open = button.getAttribute('aria-expanded') === 'true';
    button.setAttribute('aria-expanded', open ? 'false' : 'true');
    body.hidden = open;
  },

  updateInlineSummary(cart = this.lastCart) {
    if (!this.drawer || !cart) return;
    const subtitle = this.drawer.querySelector('[data-cart-summary-subtitle]');
    if (subtitle) {
      subtitle.textContent = `${cart.item_count} item${cart.item_count === 1 ? '' : 's'} - view details`;
    }

    const body = this.drawer.querySelector('[data-cod-cart-summary-body]');
    if (!body) return;
    const discountRows = (cart.cart_level_discount_applications || []).map((discount) => `
      <div class="bl-cart__ordsum-line bl-cart__ordsum-line--disc">
        <span>${this.escapeHtml(discount.title)}</span>
        <span>${this.formatMoneyHtml(Number(discount.total_allocated_amount || 0) * -1)}</span>
      </div>
    `).join('');
    // items_subtotal_price is already NET of the free item, so showing it as "Subtotal"
    // beside an equal "Total" hides the saving entirely. Show the gross and break the
    // saving out on its own row. The BXGY allocates at LINE level, so it never appears
    // in cart_level_discount_applications -- discountRows alone would render nothing.
    const bogoSaved = Math.max(0, Number(cart.original_total_price || 0) - Number(cart.total_price || 0));
    const savingsRow = bogoSaved > 0
      ? `<div class="bl-cart__ordsum-line bl-cart__ordsum-line--disc"><span>Offer savings</span><span>${this.formatMoneyHtml(-bogoSaved)}</span></div>`
      : '';
    body.innerHTML = `
      <div class="bl-cart__ordsum-line"><span>Subtotal</span><span>${this.formatMoneyHtml(cart.original_total_price)}</span></div>
      ${savingsRow}
      ${discountRows}
      <div class="bl-cart__ordsum-line bl-cart__ordsum-line--muted"><span>Taxes</span><span>Calculated at checkout</span></div>
      <div class="bl-cart__ordsum-line bl-cart__ordsum-line--muted"><span>Shipping</span><span>Calculated at checkout</span></div>
      <div class="bl-cart__ordsum-line bl-cart__ordsum-grand"><span>Total</span><span>${this.formatMoneyHtml(cart.total_price)}</span></div>
    `;
  },

  copyCouponCode(button) {
    const code = String(button.getAttribute('data-copy-code') || '').trim();
    if (!code) return;
    const done = () => {
      const previous = button.textContent;
      button.textContent = 'Copied';
      button.classList.add('is-copied');
      window.setTimeout(() => {
        button.textContent = previous || 'Copy';
        button.classList.remove('is-copied');
      }, 1500);
    };
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(code).then(done).catch(done);
    } else {
      done();
    }
  },

  openCouponModal() {
    const modal = document.querySelector('[data-cart-coupon-modal]');
    if (!modal) return;
    this.clearCouponError();
    modal.hidden = false;
    modal.classList.add('is-open');
    theme.utils.lockScroll();
    this.couponCleanup?.();
    this.couponCleanup = theme.utils.trapFocus(modal);
    modal.querySelector('[data-cart-coupon-input]')?.focus();
    this.updateCouponState();
  },

  closeCouponModal() {
    const modal = document.querySelector('[data-cart-coupon-modal]');
    if (!modal) return;
    modal.hidden = true;
    modal.classList.remove('is-open');
    this.couponCleanup?.();
    this.couponCleanup = null;
    if (!document.querySelector('.cod-modal.is-open')) theme.utils.unlockScroll();
  },

  clearCouponError() {
    const error = document.querySelector('[data-cart-coupon-error]');
    if (!error) return;
    error.hidden = true;
    error.textContent = '';
  },

  setCouponError(message) {
    const error = document.querySelector('[data-cart-coupon-error]');
    if (!error) return;
    error.textContent = message;
    error.hidden = false;
  },

  async applyCoupon(rawCode) {
    const code = String(rawCode || '').trim().toUpperCase();
    if (!code) {
      this.setCouponError('Enter a coupon code.');
      return;
    }

    document.querySelectorAll('[data-cart-coupon-submit], [data-cart-coupon-apply]').forEach((button) => {
      button.disabled = true;
    });

    try {
      const response = await fetch(`/discount/${encodeURIComponent(code)}`, { credentials: 'same-origin', cache: 'no-store' });
      if (!response.ok) throw new Error(`Discount request failed: ${response.status}`);
      const cart = await this.fetchCart();
      const applied = this.discountCodes(cart).has(code);
      if (!applied) {
        this.lastCart = cart;
        if (code === this.checkoutCouponCode) {
          this.markCheckoutCoupon(code);
          this.closeCouponModal();
          this.showCouponConfirm(code, cart, `${code} will apply at Razorpay checkout.`);
        } else {
          this.updateCouponState(cart);
          this.setCouponError(`Coupon ${code} could not be applied.`);
        }
        return;
      }

      this.markCheckoutCoupon(code);
      this.render(cart);
      theme.events.emit('theme:cart:updated', { cart });
      this.closeCouponModal();
      if (document.body.classList.contains('template-cart')) {
        document.body.dataset.cartReloadAfterCoupon = 'true';
      }
      this.showCouponConfirm(code, cart);
    } catch (error) {
      console.warn('Coupon apply failed', error);
      if (code === this.checkoutCouponCode) {
        const cart = this.lastCart || await this.fetchCart().catch(() => null);
        this.markCheckoutCoupon(code);
        this.closeCouponModal();
        this.showCouponConfirm(code, cart || { total_price: 0 }, `${code} will apply at Razorpay checkout.`);
      } else {
        this.setCouponError(`Coupon ${code} could not be applied.`);
      }
    } finally {
      document.querySelectorAll('[data-cart-coupon-submit], [data-cart-coupon-apply]').forEach((button) => {
        button.disabled = false;
      });
      this.updateCouponState(this.lastCart);
    }
  },

  showCouponConfirm(code, cart, message) {
    const modal = document.querySelector('[data-cart-coupon-confirm]');
    if (!modal) return;
    modal.querySelector('[data-cart-coupon-confirm-text]').textContent = message || `${code} is active on this cart.`;
    modal.querySelector('[data-cart-coupon-confirm-total]').innerHTML = this.formatMoneyHtml(cart.total_price);
    modal.hidden = false;
    modal.classList.add('is-open');
    theme.utils.lockScroll();
    modal.querySelector('[data-cart-coupon-confirm-close]')?.focus();
  },

  closeCouponConfirm() {
    const modal = document.querySelector('[data-cart-coupon-confirm]');
    if (!modal) return;
    modal.hidden = true;
    modal.classList.remove('is-open');
    if (!document.querySelector('.cod-modal.is-open')) theme.utils.unlockScroll();
    if (document.body.dataset.cartReloadAfterCoupon === 'true') {
      delete document.body.dataset.cartReloadAfterCoupon;
      window.location.reload();
    }
  },

  async populatePriceBreakdown(modal) {
    if (!modal) return;
    const lines = modal.querySelector('[data-price-breakdown-lines]');
    const grand = modal.querySelector('[data-price-breakdown-grand]');
    if (!lines || !grand) return;

    const cart = this.lastCart || await this.fetchCart();
    this.lastCart = cart;
    lines.innerHTML = '';

    if (!cart.items?.length) {
      lines.hidden = true;
      grand.hidden = true;
      return;
    }

    const itemRows = cart.items.map((item) => `
      <div class="CODPriceBreakdownLine">
        <span>${this.escapeHtml(item.product_title)} x ${Number(item.quantity || 0)}</span>
        <strong>${this.formatMoneyHtml(item.final_line_price)}</strong>
      </div>
    `).join('');
    const discountRows = (cart.cart_level_discount_applications || []).map((discount) => `
      <div class="CODPriceBreakdownLine CODPriceBreakdownLine--save">
        <span>${this.escapeHtml(discount.title)}</span>
        <strong>${this.formatMoneyHtml(Number(discount.total_allocated_amount || 0) * -1)}</strong>
      </div>
    `).join('');

    lines.innerHTML = `
      <div class="CODPriceBreakdownLine"><span>Subtotal</span><strong>${this.formatMoneyHtml(cart.original_total_price || cart.items_subtotal_price || cart.total_price)}</strong></div>
      ${itemRows}
      ${discountRows}
      <div class="CODPriceBreakdownLine"><span>Taxes</span><strong>Included</strong></div>
      <div class="CODPriceBreakdownLine"><span>Shipping</span><strong>Calculated at checkout</strong></div>
    `;
    grand.innerHTML = `<span>Total</span><strong>${this.formatMoneyHtml(cart.total_price)}</strong>`;
    lines.hidden = false;
    grand.hidden = false;
  },

  updateCartCount(count) {
    document.querySelectorAll('[data-cart-count]').forEach((element) => {
      element.textContent = count;
      element.hidden = count === 0;
    });
  },

  formatMoneyHtml(cents) {
    const value = Number(cents || 0);
    const amount = new Intl.NumberFormat('en-IN', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(Math.abs(value) / 100);
    const sign = value < 0 ? '-' : '';
    return `<span class="CODPrice rv-price">${sign}<sup class="CODPriceCurrency rv-cur">INR</sup>${amount}</span>`;
  }
};

theme.featuredProducts = {
  init() {
    document.querySelectorAll('[data-featured-products]').forEach((section) => {
      if (section.dataset.carouselInitialized === 'true') return;

      const scroller = section.querySelector('[data-featured-scroller]');
      const previous = section.querySelector('[data-featured-prev]');
      const next = section.querySelector('[data-featured-next]');
      if (!scroller || !previous || !next) return;

      section.dataset.carouselInitialized = 'true';

      const scrollByAmount = () => Math.max(scroller.clientWidth * 0.85, 280);

      previous.addEventListener('click', () => {
        scroller.scrollBy({ left: -scrollByAmount(), behavior: 'smooth' });
      });

      next.addEventListener('click', () => {
        scroller.scrollBy({ left: scrollByAmount(), behavior: 'smooth' });
      });
    });
  }
};

theme.productRecommendations = {
  init() {
    document.querySelectorAll('[data-product-recommendations]').forEach((section) => {
      if (section.dataset.recommendationsLoaded === 'true') return;

      const url = section.dataset.url;
      if (!url) return;

      section.dataset.recommendationsLoaded = 'true';

      fetch(url)
        .then((response) => {
          if (!response.ok) throw new Error(response.status);
          return response.text();
        })
        .then((html) => {
          const documentFragment = new DOMParser().parseFromString(html, 'text/html');
          const recommendations = documentFragment.querySelector('[data-product-recommendations]');

          if (recommendations && recommendations.innerHTML.trim().length) {
            section.innerHTML = recommendations.innerHTML;
            section.setAttribute('data-featured-products', '');
            theme.featuredProducts.init();
            theme.animations.init();
          }
        })
        .catch(() => {
          section.hidden = true;
        });
    });
  }
};

theme.footer = {
  init() {
    const accordions = document.querySelectorAll('[data-footer-accordion]');
    if (!accordions.length) return;

    const desktopQuery = window.matchMedia('(min-width: 750px)');

    const syncAccordions = () => {
      accordions.forEach((accordion) => {
        accordion.open = desktopQuery.matches;
      });
    };

    syncAccordions();
    if (desktopQuery.addEventListener) {
      desktopQuery.addEventListener('change', syncAccordions);
    } else {
      desktopQuery.addListener(syncAccordions);
    }
  }
};

theme.collectionFilters = {
  init() {
    document.querySelectorAll('[data-filter-drawer]').forEach((drawer) => {
      const openButtons = document.querySelectorAll('[data-filter-open]');
      const closeButtons = drawer.querySelectorAll('[data-filter-close]');

      const open = () => {
        drawer.setAttribute('aria-hidden', 'false');
        drawer.classList.add('is-open');
        theme.utils.lockScroll();
      };

      const close = () => {
        drawer.setAttribute('aria-hidden', 'true');
        drawer.classList.remove('is-open');
        theme.utils.unlockScroll();
      };

      openButtons.forEach((button) => button.addEventListener('click', open));
      closeButtons.forEach((button) => button.addEventListener('click', close));
      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') close();
      });
    });
  }
};

theme.luxuryMotion = {
  observer: null,

  init() {
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const selectors = [
      '.announcement-bar',
      '.theme-header__bar',
      '.como-homepage__hero-placeholder',
      '.como-homepage__category-card',
      '.como-homepage__heading',
      '.como-homepage__story-copy',
      '.como-homepage__story-media',
      '.como-homepage__ugc-card',
      '.como-homepage__store-slide',
      '.main-collection__header',
      '.main-collection__mobile-bar',
      '.collection-filters',
      '.main-collection__grid .product-card',
      '.product-grid-section__grid .product-card',
      '.featured-products__item',
      '.main-product__media-item',
      '.main-product__info > *',
      '.product-details-panel__copy',
      '.product-details-panel__item',
      '.product-reviews__summary',
      '.product-reviews__score-card',
      '.product-reviews__card',
      '.product-reviews__trust-row span',
      '.recommended-products__header',
      '.recommended-products__item',
      '.main-search__hero',
      '.main-search__section',
      '.main-search__category-card',
      '.main-search__content-card',
      '.site-footer__brand',
      '.site-footer__group',
      '.site-footer__bottom',
      '.como-info-page__hero',
      '.como-info-page__hero-panel',
      '.como-info-page__section-head',
      '.como-info-page__step',
      '.como-info-page__card',
      '.como-info-page__callout',
      '.como-info-page__visual',
      '.como-info-page__facts span',
      '.como-info-page__contact-shell',
      '.como-info-page__form'
    ];

    const elements = [];
    selectors.forEach((selector) => {
      document.querySelectorAll(selector).forEach((element, index) => {
        if (element.dataset.luxuryPrepared === 'true') return;
        element.dataset.luxuryPrepared = 'true';
        element.classList.add('luxury-reveal');
        if (/media|image|story|visual|ugc|slide|product-card|recommended/i.test(selector)) {
          element.classList.add('luxury-reveal--image');
        }
        element.style.setProperty('--luxury-delay', Math.min(index * 55, 420) + 'ms');
        elements.push(element);
      });
    });

    if (!elements.length) return;

    if (reduceMotion || !('IntersectionObserver' in window)) {
      elements.forEach((element) => element.classList.add('is-luxury-visible'));
      return;
    }

    if (!this.observer) {
      this.observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-luxury-visible');
          this.observer.unobserve(entry.target);
        });
      }, { rootMargin: '0px 0px -10% 0px', threshold: 0.12 });
    }

    elements.forEach((element) => this.observer.observe(element));

    window.requestAnimationFrame(() => this.revealInViewport());
    window.setTimeout(() => this.revealInViewport(), 140);

    if (!this.fallbackPrepared) {
      this.fallbackPrepared = true;
      let ticking = false;
      const run = () => {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(() => {
          this.revealInViewport();
          ticking = false;
        });
      };
      window.addEventListener('scroll', run, { passive: true });
      window.addEventListener('resize', run);
    }
  },

  revealInViewport() {
    const viewport = window.innerHeight || document.documentElement.clientHeight || 1;
    document.querySelectorAll('.luxury-reveal:not(.is-luxury-visible)').forEach((element) => {
      const rect = element.getBoundingClientRect();
      if (rect.bottom < -60 || rect.top > viewport * 1.08) return;
      element.classList.add('is-luxury-visible');
      this.observer?.unobserve?.(element);
    });
  }
};



// BA header search compact
theme.headerSearch = {
  collectionRoutes: new Map([
    ['chain', '/collections/chains'],
    ['chains', '/collections/chains'],
    ['cuban', '/collections/cuban-chains'],
    ['cuban chain', '/collections/cuban-chains'],
    ['tennis', '/collections/tennis-chains'],
    ['tennis chain', '/collections/tennis-chains'],
    ['bracelet', '/collections/bracelets'],
    ['bracelets', '/collections/bracelets'],
    ['cuban bracelet', '/collections/cuban-bracelets'],
    ['tennis bracelet', '/collections/tennis-bracelets'],
    ['beaded', '/collections/beaded-bracelets'],
    ['beaded bracelet', '/collections/beaded-bracelets'],
    ['ring', '/collections/rings'],
    ['rings', '/collections/rings'],
    ['signet', '/collections/signet-rings'],
    ['signet ring', '/collections/signet-rings'],
    ['pave', '/collections/pave-rings'],
    ['pave ring', '/collections/pave-rings'],
    ['earring', '/collections/earrings'],
    ['earrings', '/collections/earrings'],
    ['stud', '/collections/stud-earrings'],
    ['studs', '/collections/stud-earrings'],
    ['hoop', '/collections/hoop-earrings'],
    ['hoops', '/collections/hoop-earrings'],
    ['charm', '/collections/charms'],
    ['charms', '/collections/charms'],
    ['pendant', '/collections/charms'],
    ['pendants', '/collections/charms'],
    ['sale', '/collections/sale']
  ]),

  normalize(value) {
    return value
      .trim()
      .toLowerCase()
      .replace(/&/g, 'and')
      .replace(/[^a-z0-9-]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  },

  init() {
    document.querySelectorAll('[data-header-search]').forEach((form) => {
      if (form.dataset.headerSearchReady === 'true') return;
      form.dataset.headerSearchReady = 'true';

      const toggle = form.querySelector('[data-header-search-toggle]');
      const input = form.querySelector('[data-header-search-input]');
      const toggleText = form.querySelector('[data-header-search-toggle-text]');
      if (!toggle || !input) return;

      const searchLabel = toggle.getAttribute('data-search-label') || toggleText?.textContent || 'Search';
      const closeLabel = toggle.getAttribute('data-close-label') || 'Close';
      input.setAttribute('tabindex', '-1');

      const setToggleLabel = (label) => {
        if (toggleText) toggleText.textContent = label;
        toggle.setAttribute('aria-label', label);
      };

      const open = () => {
        form.classList.add('is-open');
        toggle.setAttribute('aria-expanded', 'true');
        input.removeAttribute('tabindex');
        setToggleLabel(closeLabel);
        requestAnimationFrame(() => input.focus());
      };

      const close = () => {
        form.classList.remove('is-open');
        toggle.setAttribute('aria-expanded', 'false');
        input.setAttribute('tabindex', '-1');
        setToggleLabel(searchLabel);
        input.blur();
        toggle.blur();
      };

      toggle.addEventListener('click', (event) => {
        event.preventDefault();
        form.classList.contains('is-open') ? close() : open();
      });

      form.addEventListener('submit', (event) => {
        const query = this.normalize(input.value);
        if (!query) {
          event.preventDefault();
          open();
          return;
        }

        const collectionRoute = this.collectionRoutes.get(query);
        if (collectionRoute) {
          event.preventDefault();
          window.location.assign(collectionRoute);
        }
      });

      input.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          event.preventDefault();
          close();
        }
      });

      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && form.classList.contains('is-open')) close();
      });

      document.addEventListener('click', (event) => {
        if (form.classList.contains('is-open') && !form.contains(event.target)) close();
      });
    });
  }
};

theme.inrPrices = {
  pattern: /(Rs\.|₹)\s*([\d,]+(?:\.\d{1,2})?)/g,
  skipSelector: 'script, style, noscript, textarea, input, select, option, [data-cart-count], [data-wishlist-count], .rv-cur, .CODPriceCurrency',

  replaceTextNode(node) {
    const text = node.nodeValue;
    this.pattern.lastIndex = 0;
    if (!text || !this.pattern.test(text)) return;
    this.pattern.lastIndex = 0;

    const fragment = document.createDocumentFragment();
    let cursor = 0;
    let match;
    while ((match = this.pattern.exec(text))) {
      if (match.index > cursor) {
        fragment.append(document.createTextNode(text.slice(cursor, match.index)));
      }
      const wrapper = document.createElement('span');
      wrapper.className = 'CODPrice rv-price';
      const currency = document.createElement('sup');
      currency.className = 'CODPriceCurrency rv-cur';
      currency.textContent = 'INR';
      wrapper.append(currency, document.createTextNode(match[2]));
      fragment.append(wrapper);
      cursor = match.index + match[0].length;
    }
    if (cursor < text.length) {
      fragment.append(document.createTextNode(text.slice(cursor)));
    }
    node.replaceWith(fragment);
  },

  sweep(root = document.body) {
    if (!root) return;
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
      acceptNode: (node) => {
        const parent = node.parentElement;
        if (!parent || parent.closest(this.skipSelector)) return NodeFilter.FILTER_REJECT;
        this.pattern.lastIndex = 0;
        const matches = this.pattern.test(node.nodeValue || '');
        this.pattern.lastIndex = 0;
        return matches ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT;
      }
    });
    const nodes = [];
    while (walker.nextNode()) nodes.push(walker.currentNode);
    nodes.forEach((node) => this.replaceTextNode(node));
    this.pattern.lastIndex = 0;
  },

  init() {
    this.sweep();
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.TEXT_NODE) this.replaceTextNode(node);
          if (node.nodeType === Node.ELEMENT_NODE) this.sweep(node);
        });
      });
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }
};

theme.productForms = {
  init() {
    document.querySelectorAll('.CODProductForm').forEach((form) => {
      if (form.dataset.productFormReady === 'true') return;
      form.dataset.productFormReady = 'true';

      const variantsScript = form.querySelector('[data-variant-json]');
      const idInput = form.querySelector('[data-selected-variant-id]');
      const addButton = form.querySelector('[data-add-button]');
      const stickyButton = form.id ? document.querySelector(`[data-pdp-sticky-submit][data-product-form="${form.id}"]`) : null;
      const quantityInput = form.querySelector('[data-product-quantity]');
      const priceTargets = Array.from(document.querySelectorAll('[data-product-price-target], [data-product-sticky-price]'));
      if (!variantsScript || !idInput) return;

      let variants = [];
      try {
        variants = JSON.parse(variantsScript.textContent || '[]');
      } catch (error) {
        variants = [];
      }

      const optionGroups = Array.from(form.querySelectorAll('[data-option-index]'))
        .sort((a, b) => Number(a.dataset.optionIndex) - Number(b.dataset.optionIndex));

      const selectedOptions = () => optionGroups.map((group) => {
        const checked = group.querySelector('[data-option-value]:checked');
        return checked ? checked.value : '';
      });

      const formatMoneyHtml = (cents) => {
        const amount = new Intl.NumberFormat('en-IN', {
          minimumFractionDigits: 0,
          maximumFractionDigits: 2
        }).format(Number(cents || 0) / 100);
        return `<sup class="CODPriceCurrency rv-cur">INR</sup>${amount}`;
      };

      const update = () => {
        const options = selectedOptions();
        const variant = variants.find((item) => {
          if (!Array.isArray(item.options)) return false;
          return options.every((value, index) => !value || item.options[index] === value);
        }) || variants.find((item) => item.available) || variants[0];

        if (!variant) return;
        idInput.value = variant.id;
        if (variant.price != null) {
          priceTargets.forEach((target) => {
            target.innerHTML = formatMoneyHtml(variant.price);
          });
        }
        if (addButton) addButton.disabled = !variant.available;
        if (stickyButton) stickyButton.disabled = !variant.available;
      };

      form.addEventListener('change', (event) => {
        if (event.target.matches('[data-option-value]')) update();
      });

      form.addEventListener('click', (event) => {
        const decrease = event.target.closest('[data-product-qty-decrease]');
        const increase = event.target.closest('[data-product-qty-increase]');
        if (!decrease && !increase) return;
        event.preventDefault();
        const current = Math.max(1, Number(quantityInput?.value || 1));
        if (quantityInput) quantityInput.value = String(increase ? current + 1 : Math.max(1, current - 1));
      });

      stickyButton?.addEventListener('click', (event) => {
        event.preventDefault();
        if (stickyButton.disabled || addButton?.disabled) return;
        addButton?.click();
      });

      update();
    });
  }
};

theme.wishlist = {
  key: 'city_of_domes_wishlist_v1',

  read() {
    try {
      const list = JSON.parse(localStorage.getItem(this.key) || '[]');
      return Array.isArray(list) ? list : [];
    } catch (error) {
      return [];
    }
  },

  write(list) {
    try {
      localStorage.setItem(this.key, JSON.stringify(list));
    } catch (error) {}
    document.dispatchEvent(new CustomEvent('wishlist:change'));
  },

  itemFromButton(button) {
    return {
      id: button.getAttribute('data-product-id') || '',
      handle: button.getAttribute('data-product-handle') || '',
      title: button.getAttribute('data-product-title') || '',
      url: button.getAttribute('data-product-url') || '',
      price: button.getAttribute('data-product-price') || '',
      image: button.getAttribute('data-product-image') || ''
    };
  },

  has(id) {
    return this.read().some((item) => String(item.id) === String(id));
  },

  toggle(button) {
    const item = this.itemFromButton(button);
    if (!item.id) return;
    const list = this.read();
    const index = list.findIndex((entry) => String(entry.id) === String(item.id));
    if (index === -1) {
      list.push(item);
    } else {
      list.splice(index, 1);
    }
    this.write(list);
  },

  count() {
    return this.read().length;
  },

  escapeHtml(value) {
    return String(value || '').replace(/[&<>"']/g, (char) => ({
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#39;'
    }[char]));
  },

  formatMoneyHtml(cents) {
    const amount = new Intl.NumberFormat('en-IN', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(Number(cents || 0) / 100);
    return `<span class="CODPrice rv-price"><sup class="CODPriceCurrency rv-cur">INR</sup>${amount}</span>`;
  },

  cardHtml(item) {
    const title = this.escapeHtml(item.title);
    const handle = this.escapeHtml(item.handle);
    const url = item.url || `/products/${handle}`;
    const image = item.image ? `<img class="CODProductCardImage myugen-collection-card__image" src="${this.escapeHtml(item.image)}" alt="${title}" width="760" height="1140" loading="lazy" decoding="async">` : '';
    return `
      <article class="CODProductCard myugen-collection-card" data-component="CODProductCard" data-component-tier="tissue" data-wishlist-card>
        <div class="CODProductCardMedia myugen-collection-card__media" data-component="CODProductCardMedia">
          <a class="CODProductCardMediaLink myugen-collection-card__media-link" href="${this.escapeHtml(url)}" aria-label="${title}">
            ${image}
            <span class="u-visually-hidden">View ${title}</span>
          </a>
          <button class="CODWishlistButton myugen-collection-card__wishlist" type="button" data-wishlist-toggle data-product-id="${this.escapeHtml(item.id)}" data-product-handle="${handle}" data-product-title="${title}" data-product-url="${this.escapeHtml(url)}" data-product-price="${this.escapeHtml(item.price)}" data-product-image="${this.escapeHtml(item.image)}" aria-label="Remove ${title} from wishlist" aria-pressed="true">
            <svg viewBox="0 0 24 24" focusable="false" aria-hidden="true"><path d="M12 21s-7.5-4.6-10-9.3C.5 8.5 2 5 5.2 5 7 5 8.4 6 12 9c3.6-3 5-4 6.8-4C22 5 23.5 8.5 22 11.7 19.5 16.4 12 21 12 21z"></path></svg>
          </button>
        </div>
        <a class="CODProductCardBody myugen-collection-card__info" href="${this.escapeHtml(url)}" aria-label="${title}">
          <h2 class="CODProductCardTitle myugen-collection-card__title">${title}</h2>
          <span class="CODProductCardPrice myugen-collection-card__price">${this.formatMoneyHtml(item.price)}</span>
        </a>
      </article>
    `;
  },

  syncButtons() {
    document.querySelectorAll('[data-wishlist-toggle]').forEach((button) => {
      const item = this.itemFromButton(button);
      const active = this.has(item.id);
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', active ? 'true' : 'false');
      if (item.title) {
        button.setAttribute('aria-label', `${active ? 'Remove' : 'Add'} ${item.title} ${active ? 'from' : 'to'} wishlist`);
      }
    });
  },

  syncCounts() {
    const count = this.count();
    document.querySelectorAll('[data-wishlist-count]').forEach((element) => {
      element.textContent = count;
      element.hidden = count === 0 && !element.closest('[data-wishlist-section]');
    });
  },

  renderPage() {
    const grid = document.querySelector('[data-wishlist-grid]');
    const empty = document.querySelector('[data-wishlist-empty]');
    if (!grid || !empty) return;
    const list = this.read();
    grid.innerHTML = list.map((item) => this.cardHtml(item)).join('');
    empty.hidden = list.length > 0;
    grid.hidden = list.length === 0;
  },

  syncAll() {
    this.syncCounts();
    this.renderPage();
    this.syncButtons();
  },

  init() {
    document.addEventListener('click', (event) => {
      const button = event.target.closest('[data-wishlist-toggle]');
      if (!button) return;
      event.preventDefault();
      event.stopPropagation();
      this.toggle(button);
    });

    document.addEventListener('wishlist:change', () => this.syncAll());
    this.syncAll();
    window.ComoWishlist = {
      read: () => this.read(),
      has: (id) => this.has(id),
      count: () => this.count(),
      sync: () => this.syncAll()
    };
  }
};

theme.featuredRing = {
  init() {
    document.querySelectorAll('[data-cod-featured-ring]').forEach((section) => {
      if (section.dataset.codRingReady === 'true') return;
      const ring = section.querySelector('[data-cod-ring]');
      const cards = Array.from(section.querySelectorAll('[data-cod-ring-card]'));
      if (!ring || cards.length < 2) return;

      section.dataset.codRingReady = 'true';
      const activeLink = section.querySelector('[data-cod-ring-active]');
      const activeType = section.querySelector('[data-cod-ring-active-type]');
      const activeTitle = section.querySelector('[data-cod-ring-active-title]');
      const activePrice = section.querySelector('[data-cod-ring-active-price]');
      let active = Math.floor(cards.length / 2);
      let cardWidth = 260;
      let dragStart = 0;
      let dragging = false;
      let timer = null;

      const updateActive = () => {
        const card = cards[active];
        if (!card) return;
        if (activeLink) activeLink.href = card.getAttribute('href') || '#';
        if (activeType) activeType.textContent = card.dataset.type || '';
        if (activeTitle) activeTitle.textContent = card.dataset.title || '';
        if (activePrice) activePrice.textContent = card.dataset.price || '';
      };

      const layout = () => {
        cardWidth = cards[0].offsetWidth || 260;
        const spacing = cardWidth * 0.66;
        const depth = cardWidth * 0.9;
        const angle = 40;
        const length = cards.length;

        cards.forEach((card, index) => {
          let offset = index - active;
          if (offset > length / 2) offset -= length;
          if (offset < -length / 2) offset += length;
          const distance = Math.abs(offset);
          card.style.transform = `translate(-50%, -50%) translateX(${offset * spacing}px) translateZ(${-distance * depth * 0.5}px) rotateY(${offset * -angle}deg) scale(${Math.max(0.62, 1 - distance * 0.14)})`;
          card.style.opacity = distance > 2 ? '0' : (offset === 0 ? '1' : '0.52');
          card.style.zIndex = String(100 - distance);
          card.style.pointerEvents = distance > 2 ? 'none' : 'auto';
          card.classList.toggle('is-active', offset === 0);
        });

        updateActive();
      };

      const go = (index) => {
        active = ((index % cards.length) + cards.length) % cards.length;
        layout();
      };

      const start = () => {
        window.clearInterval(timer);
        timer = window.setInterval(() => go(active + 1), 4500);
      };
      const stop = () => window.clearInterval(timer);

      cards.forEach((card, index) => {
        card.addEventListener('click', (event) => {
          if (index === active) return;
          event.preventDefault();
          go(index);
        });
      });

      section.querySelector('[data-cod-ring-prev]')?.addEventListener('click', () => go(active - 1));
      section.querySelector('[data-cod-ring-next]')?.addEventListener('click', () => go(active + 1));
      ring.addEventListener('pointerdown', (event) => {
        dragging = true;
        dragStart = event.clientX;
      });
      window.addEventListener('pointerup', (event) => {
        if (!dragging) return;
        dragging = false;
        const delta = event.clientX - dragStart;
        if (Math.abs(delta) > 45) go(active + (delta < 0 ? 1 : -1));
      });
      ring.addEventListener('wheel', (event) => {
        if (Math.abs(event.deltaX) <= Math.abs(event.deltaY)) return;
        event.preventDefault();
        go(active + (event.deltaX > 0 ? 1 : -1));
      }, { passive: false });
      ring.addEventListener('mouseenter', stop);
      ring.addEventListener('mouseleave', start);
      window.addEventListener('resize', layout);

      layout();
      start();
    });
  }
};

theme.cardCarousel = {
  ready: false,
  observer: null,

  setSlide(card, index) {
    const track = card.querySelector('[data-rv-track]');
    const slides = card.querySelectorAll('.rv-cardcar__slide');
    const bar = card.querySelector('[data-rv-barfill]');
    const count = slides.length;
    if (!track || count < 2) return;

    const nextIndex = ((index % count) + count) % count;
    card._rvIndex = nextIndex;
    track.style.setProperty('transform', `translate3d(${-nextIndex * 100}%, 0, 0)`, 'important');
    if (bar) bar.style.width = `${((nextIndex + 1) / count) * 100}%`;
  },

  initCard(card) {
    if (!card || card.dataset.rvReady === 'true') return;
    const slides = card.querySelectorAll('.rv-cardcar__slide');
    if (slides.length < 2) return;

    card.dataset.rvReady = 'true';
    this.setSlide(card, 0);

    const productCard = card.closest('.CODProductCard') || card;
    const resetSlide = () => this.setSlide(card, 0);

    card.querySelector('[data-rv-prev]')?.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      this.setSlide(card, (card._rvIndex || 0) - 1);
    });

    card.querySelector('[data-rv-next]')?.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      this.setSlide(card, (card._rvIndex || 0) + 1);
    });

    let startX = null;
    let startY = null;
    let baseIndex = 0;
    let moved = false;

    card.addEventListener('touchstart', (event) => {
      startX = event.touches[0].clientX;
      startY = event.touches[0].clientY;
      baseIndex = card._rvIndex || 0;
      moved = false;
    }, { passive: true });

    card.addEventListener('touchmove', (event) => {
      if (startX === null || startY === null) return;
      const dx = event.touches[0].clientX - startX;
      const dy = event.touches[0].clientY - startY;
      if (Math.abs(dx) <= Math.abs(dy) || Math.abs(dx) <= 10) return;
      moved = true;
      const step = Math.round(-dx / (card.getBoundingClientRect().width * 0.5));
      this.setSlide(card, baseIndex + step);
    }, { passive: true });

    card.addEventListener('touchend', () => {
      startX = null;
      startY = null;
    }, { passive: true });

    card.addEventListener('click', (event) => {
      if (!moved) return;
      event.preventDefault();
      event.stopPropagation();
      moved = false;
    }, true);

    productCard.addEventListener('pointerleave', resetSlide);
    productCard.addEventListener('mouseleave', resetSlide);
    productCard.addEventListener('focusout', () => {
      window.setTimeout(() => {
        if (!productCard.contains(document.activeElement)) resetSlide();
      }, 0);
    });
  },

  scan(root = document) {
    if (root.matches?.('[data-rv-cardcar]')) this.initCard(root);
    root.querySelectorAll?.('[data-rv-cardcar]').forEach((card) => this.initCard(card));
  },

  init() {
    this.scan(document);
    if (this.ready) return;
    this.ready = true;
    this.observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1) this.scan(node);
        });
      });
    });
    if (document.body) this.observer.observe(document.body, { childList: true, subtree: true });
  }
};

theme.productCardQuickAdd = {
  ready: false,

  init() {
    if (this.ready) return;
    this.ready = true;

    document.addEventListener('click', async (event) => {
      const button = event.target.closest('[data-card-variant-add]');
      if (!button) return;

      event.preventDefault();
      event.stopPropagation();

      const variantId = button.getAttribute('data-card-variant-add');
      if (!variantId || button.disabled) return;

      button.disabled = true;
      button.classList.add('is-loading');
      try {
        await theme.cartDrawer.fetchJson('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: variantId, quantity: 1 })
        });
        await theme.cartDrawer.open();
      } catch (error) {
        console.warn('Product card quick add failed', error);
      } finally {
        button.disabled = false;
        button.classList.remove('is-loading');
      }
    });
  }
};

theme.quickView = {
  ready: false,
  modal: null,
  product: null,
  variant: null,
  imageIndex: 0,
  imageUrls: [],

  init() {
    if (this.ready) return;
    this.ready = true;
    this.modal = document.querySelector('[data-quick-view-modal]');
    if (!this.modal) return;

    document.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-quick-view-handle]');
      if (!trigger) return;
      event.preventDefault();
      event.stopPropagation();
      this.open(trigger.dataset.quickViewHandle);
    });

    this.modal.querySelectorAll('[data-quick-view-close]').forEach((button) => {
      button.addEventListener('click', () => this.close());
    });

    this.modal.querySelector('[data-quick-view-add]')?.addEventListener('click', () => this.add());
    this.modal.querySelector('[data-quick-view-prev]')?.addEventListener('click', () => this.showImage(this.imageIndex - 1));
    this.modal.querySelector('[data-quick-view-next]')?.addEventListener('click', () => this.showImage(this.imageIndex + 1));

    this.modal.querySelector('[data-quick-view-variants]')?.addEventListener('click', (event) => {
      const button = event.target.closest('[data-quick-view-variant]');
      if (!button || button.disabled) return;
      const variant = this.findVariant(button.getAttribute('data-quick-view-variant'));
      if (variant) this.selectVariant(variant);
    });

    this.modal.querySelector('[data-quick-view-dots]')?.addEventListener('click', (event) => {
      const button = event.target.closest('[data-quick-view-dot]');
      if (!button) return;
      this.showImage(Number(button.getAttribute('data-quick-view-dot') || 0));
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') this.close();
    });
  },

  async open(handle) {
    if (!handle || !this.modal) return;
    try {
      this.product = await theme.cartDrawer.fetchJson(`/products/${encodeURIComponent(handle)}.js`, { headers: { Accept: 'application/json' } });
    } catch (error) {
      console.warn('Quick view load failed', error);
      return;
    }
    this.variant = Array.isArray(this.product.variants)
      ? this.product.variants.find((item) => item.available) || this.product.variants[0]
      : null;

    const title = this.modal.querySelector('[data-quick-view-title]');
    const type = this.modal.querySelector('[data-quick-view-type]');
    const link = this.modal.querySelector('[data-quick-view-link]');
    const add = this.modal.querySelector('[data-quick-view-add]');
    const url = `/products/${encodeURIComponent(this.product.handle || handle)}`;

    title.textContent = this.product.title || '';
    type.textContent = this.product.type || 'Product';
    link.href = url;
    this.renderImages();
    this.renderVariants();
    this.selectVariant(this.variant);
    add.disabled = !this.variant || this.variant.available === false;

    this.modal.hidden = false;
    this.modal.classList.add('is-open');
    theme.utils.lockScroll();
    window.setTimeout(() => add.focus(), 30);
  },

  renderImages() {
    const track = this.modal.querySelector('[data-quick-view-track]');
    const dots = this.modal.querySelector('[data-quick-view-dots]');
    if (!track || !dots) return;

    this.imageUrls = Array.isArray(this.product?.images) ? this.product.images.filter(Boolean) : [];
    if (!this.imageUrls.length && this.product?.featured_image) this.imageUrls = [this.product.featured_image];

    if (!this.imageUrls.length) {
      track.innerHTML = '<div class="CODQuickViewSlide CODQuickViewSlide--empty"></div>';
      dots.innerHTML = '';
      this.showImage(0);
      return;
    }

    track.innerHTML = this.imageUrls.map((src, index) => `
      <div class="CODQuickViewSlide" aria-label="Product image ${index + 1} of ${this.imageUrls.length}">
        <img src="${theme.cartDrawer.escapeHtml(src)}" alt="${theme.cartDrawer.escapeHtml(this.product.title || '')}" width="760" height="1140" loading="lazy" decoding="async">
      </div>
    `).join('');

    dots.innerHTML = this.imageUrls.length > 1
      ? this.imageUrls.map((_, index) => `<button type="button" data-quick-view-dot="${index}" aria-label="Show image ${index + 1}"></button>`).join('')
      : '';
    this.showImage(0);
  },

  renderVariants() {
    const variants = this.product?.variants || [];
    const container = this.modal.querySelector('[data-quick-view-variants]');
    if (!container) return;

    const hasOnlyDefault = variants.length <= 1 && (!variants[0] || String(variants[0].title || '').toLowerCase() === 'default title');
    if (hasOnlyDefault) {
      container.hidden = true;
      container.innerHTML = '';
      return;
    }

    const seen = new Set();
    const variantButtons = [];
    variants.forEach((variant) => {
      const label = variant.option1 || variant.title || 'Variant';
      if (seen.has(label)) return;
      seen.add(label);
      const match = variants.find((item) => (item.option1 || item.title) === label && item.available) || variant;
      variantButtons.push(`
        <button
          type="button"
          class="CODQuickViewVariant"
          data-quick-view-variant="${match.id}"
          ${match.available ? '' : 'disabled aria-disabled="true"'}
        >${theme.cartDrawer.escapeHtml(label)}</button>
      `);
    });

    container.hidden = false;
    container.innerHTML = variantButtons.join('');
  },

  findVariant(id) {
    const variantId = String(id || '');
    return (this.product?.variants || []).find((item) => String(item.id) === variantId) || null;
  },

  selectVariant(variant) {
    this.variant = variant || null;
    const price = this.modal.querySelector('[data-quick-view-price]');
    const add = this.modal.querySelector('[data-quick-view-add]');
    const selectedId = String(this.variant?.id || '');

    if (price) price.innerHTML = theme.cartDrawer.formatMoneyHtml(this.variant?.price || this.product?.price || 0);
    if (add) add.disabled = !this.variant || this.variant.available === false;

    this.modal.querySelectorAll('[data-quick-view-variant]').forEach((button) => {
      const selected = button.getAttribute('data-quick-view-variant') === selectedId;
      button.classList.toggle('is-selected', selected);
      button.setAttribute('aria-pressed', selected ? 'true' : 'false');
    });

    const featured = this.variant?.featured_image?.src || this.variant?.featured_image || '';
    const imageIndex = this.findImageIndex(featured);
    if (imageIndex >= 0) this.showImage(imageIndex);
  },

  findImageIndex(src) {
    if (!src || !this.imageUrls.length) return -1;
    const target = this.imageKey(src);
    return this.imageUrls.findIndex((imageUrl) => {
      const key = this.imageKey(imageUrl);
      return key === target || key.endsWith(target.split('/').pop() || '') || target.endsWith(key.split('/').pop() || '');
    });
  },

  imageKey(src) {
    return String(src || '').split('?')[0].replace(/^https?:/, '');
  },

  showImage(index) {
    const count = this.imageUrls.length || 1;
    const nextIndex = ((index % count) + count) % count;
    const track = this.modal?.querySelector('[data-quick-view-track]');
    const prev = this.modal?.querySelector('[data-quick-view-prev]');
    const next = this.modal?.querySelector('[data-quick-view-next]');

    this.imageIndex = nextIndex;
    if (track) track.style.setProperty('transform', `translate3d(${-nextIndex * 100}%, 0, 0)`, 'important');
    if (prev) prev.hidden = count < 2;
    if (next) next.hidden = count < 2;

    this.modal?.querySelectorAll('[data-quick-view-dot]').forEach((button) => {
      const selected = Number(button.getAttribute('data-quick-view-dot') || 0) === nextIndex;
      button.classList.toggle('is-selected', selected);
      button.setAttribute('aria-current', selected ? 'true' : 'false');
    });
  },

  close() {
    if (!this.modal || this.modal.hidden) return;
    this.modal.hidden = true;
    this.modal.classList.remove('is-open');
    if (!document.querySelector('.cod-modal.is-open')) theme.utils.unlockScroll();
  },

  async add() {
    if (!this.variant) return;
    const add = this.modal.querySelector('[data-quick-view-add]');
    add.disabled = true;
    try {
      await theme.cartDrawer.fetchJson('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: this.variant.id, quantity: 1 })
      });
      this.close();
      await theme.cartDrawer.open();
    } catch (error) {
      console.warn('Quick view add failed', error);
    } finally {
      add.disabled = false;
    }
  }
};

theme.priceBreakdown = {
  modal: null,

  init() {
    this.modal = document.querySelector('[data-price-breakdown-modal]');
    if (!this.modal) return;
    document.addEventListener('click', (event) => {
      if (event.target.closest('[data-price-breakdown-open]')) {
        event.preventDefault();
        this.open();
      }
      if (event.target.closest('[data-price-breakdown-close]')) {
        event.preventDefault();
        this.close();
      }
    });
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') this.close();
    });
  },

  open() {
    if (!this.modal) return;
    this.modal.hidden = false;
    this.modal.classList.add('is-open');
    theme.utils.lockScroll();
    theme.cartDrawer?.populatePriceBreakdown?.(this.modal);
    this.modal.querySelector('[data-price-breakdown-close]')?.focus();
  },

  close() {
    if (!this.modal) return;
    this.modal.hidden = true;
    this.modal.classList.remove('is-open');
    if (!document.querySelector('.cod-modal.is-open')) theme.utils.unlockScroll();
  }
};

theme.sizeGuide = {
  init() {
    const modal = document.querySelector('[data-size-guide-modal]');
    if (!modal) return;
    const open = () => {
      modal.hidden = false;
      modal.classList.add('is-open');
      theme.utils.lockScroll();
      modal.querySelector('[data-size-guide-close]')?.focus();
    };
    const close = () => {
      modal.hidden = true;
      modal.classList.remove('is-open');
      theme.utils.unlockScroll();
    };
    document.addEventListener('click', (event) => {
      if (event.target.closest('[data-size-guide-open]')) {
        event.preventDefault();
        open();
      }
      if (event.target.closest('[data-size-guide-close]')) {
        event.preventDefault();
        close();
      }
    });
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') close();
    });
  }
};

theme.init = () => {
  theme.pageTransitions.init();
  theme.scroll.init();
  theme.luxuryMotion?.init?.();
  theme.animations.init();
  theme.menu.init();
  theme.headerSearch?.init?.();
  theme.inrPrices?.init?.();
  theme.productForms?.init?.();
  theme.wishlist?.init?.();
  theme.featuredRing?.init?.();
  theme.cardCarousel?.init?.();
  theme.productCardQuickAdd?.init?.();
  theme.quickView?.init?.();
  theme.priceBreakdown?.init?.();
  theme.sizeGuide?.init?.();
  theme.cartDrawer.init();
  theme.featuredProducts.init();
  theme.productRecommendations.init();
  theme.footer.init();
  theme.collectionFilters.init();
};

document.addEventListener('DOMContentLoaded', theme.init);
